import { Vue, Component, Prop } from 'vue-property-decorator'
import { debounce } from '@/lib/lodash'
import isEmpty from '@/helpers/isEmpty'
import nestedFreeze from '@/helpers/nestedFreeze'

import {
  SimpleFilterField as ISimpleFilterField,
  SelectionFilterField as ISelectionFilterField,
  FilterFieldOperator,
} from '../../../../types'

type TItem = Record<string, any>

const normalizeSearchText = (value: string) => value.trim().toLowerCase()

@Component
export default class SimpleFilterField extends Vue {
  @Prop({ type: Array, required: true })
  private readonly selectedValues!: ISelectionFilterField[]
  @Prop({ type: String, required: true })
  private readonly selectedOperator!: FilterFieldOperator
  @Prop({ type: String, default: '' })
  private readonly searchText!: string
  @Prop({ type: Object, required: true })
  private readonly data!: ISimpleFilterField<string, TItem, string, string>

  private forceSearchKey = Date.now()
  private isActive = true
  private isSearching = false
  private items: TItem[] = []

  private get isMultiple() {
    const selectedOperator = this.selectedOperator

    return this.data.multiple?.some(
      ({ operator }) => operator === selectedOperator
    )
  }

  private get itemValue() {
    return this.data.itemValue || 'value'
  }

  private get itemText() {
    return this.data.itemText || 'text'
  }

  private get hasItemDivider() {
    return this.data.hasItemDivider ?? true
  }

  private get isNeedSearch() {
    if (this.data.fetchItems) {
      return !this.items.length && this.searchText.trim().length < 3
    }

    return !this.items.length && isEmpty(this.searchText)
  }

  private selectedItemExists(item: TItem) {
    return this.selectedValues.some(
      ({ value, field }) =>
        field === this.data.name && value === item[this.itemValue]
    )
  }

  private initSearch() {
    const searchItems = debounce(
      async (searchText: string) => {
        try {
          let items = this.data.items || []

          if (this.data.fetchItems && !this.isNeedSearch) {
            items = await this.data.fetchItems({ searchText })
          }

          if (normalizeSearchText(this.searchText) === searchText) {
            const filterItem = this.data.filterItem

            if (filterItem) {
              items = items.filter((item) => filterItem({ searchText, item }))
            } else if (!this.data.fetchItems) {
              items = items.filter((item) =>
                item[this.itemText]
                  ?.toString()
                  ?.toLowerCase()
                  .includes(searchText)
              )
            }

            this.items = nestedFreeze(items)
          } else {
            this.forceSearchKey++
          }
        } catch (err) {
          console.error(err)
          this.items = []
        } finally {
          this.isSearching = false
        }
      },
      !this.data.fetchItems ? 100 : 500,
      !this.data.fetchItems
        ? {
            maxWait: 300,
          }
        : undefined
    )

    this.$watch(
      () => {
        return [
          normalizeSearchText(this.searchText),
          this.data.items?.length,
          this.isNeedSearch,
          this.forceSearchKey,
        ].join('-')
      },
      () => {
        if (this.isSearching || !this.isActive) {
          return
        }

        const searchText = normalizeSearchText(this.searchText)

        if (isEmpty(searchText)) {
          this.items = this.data.items || []
          return
        }

        this.isSearching = !!this.data.fetchItems && !this.isNeedSearch

        searchItems(searchText)
      },
      {
        immediate: true,
      }
    )

    this.$once('hook:beforeDestroy', () => {
      searchItems.cancel()
    })
  }

  private autoSelectFilterFieldItem() {
    const [item] = this.items

    if (item) {
      this.selectFilterFieldItem(item, true)
    } else if (
      !this.isSearching &&
      !this.data.fetchItems &&
      !this.data.items?.length
    ) {
      const searchText = this.searchText.trim()

      const item = {
        [this.itemText]: searchText,
        [this.itemValue]: searchText,
      }

      this.selectFilterFieldItem(item, true)
    }
  }

  private selectFilterFieldItem(item: TItem, autoSelect = false) {
    const value: ISelectionFilterField = Object.freeze({
      field: this.data.name,
      text: item[this.itemText],
      value: item[this.itemValue],
      operator: this.selectedOperator,
    })

    this.$emit('select', value, autoSelect)
  }

  private created() {
    this.initSearch()
  }

  private beforeDestroy() {
    this.isActive = false
  }
}
